/**
 * @author: lv.chen
 * @date: 2025/1/14
 * @desc: DataSourceExplanation HTML版本组件
 *
 * 优化方案：支持HTML格式的数据源解释内容
 * - 复用原组件的状态管理和交互逻辑
 * - 使用 dangerouslySetInnerHTML 渲染HTML内容
 * - 不改动原组件，完全增量实现
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import { useIntl } from 'umi';
import SvgIcon from '@home/components/SvgIcon';
import CardSlotV2 from '@intelligence/components/CardSlotV2';
import SourceExplanation from '@intelligence/components/SummaryCard/components/SourceExplanation';
import { getSanitizeHTMLString } from '@intelligence/v3/utils/common';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { getDataSourceExplanationInitialState, setDataSourceExplanationInitialState } from './utils';

interface IDataSourceExplanationHtmlProps extends IBaseProps {
  /**
   * 业务主键
   */
  domain: string;
  /**
   * 展开/折叠按钮文案
   */
  triggerBtnText?: string | React.ReactNode;
  /**
   * 数据源名称列表（用于展示数据源图标和标题）
   * 例如：['MScience', 'NPD', 'GSD']
   */
  dataSourceList: string[];
  /**
   * 数据源HTML内容映射
   * 例如：{ MScience: 'Provides <span class="highlight">...</span>', ... }
   */
  htmlContentMap: IRecord<string>;
  /**
   * 触发数据源解释内容展示、隐藏的按钮容器dom
   */
  trigger: React.RefObject<HTMLElement>;
  /**
   * 数据源解释内容展示的容器dom
   */
  body: React.RefObject<HTMLElement>;
}

/**
 * 数据源HTML内容渲染组件
 */
const DataSourceHtmlContent: React.FC<{
  dataSourceList: string[];
  htmlContentMap: IRecord<string>;
}> = ({ dataSourceList, htmlContentMap }) => {
  return (
    <>
      {dataSourceList.map((dataSource, index) => (
        <div className="summary-scource-content" key={index}>
          {/* 数据源图标和标题 */}
          <div className="summary-source-title-content">
            <img src={getDataSourceIcon(dataSource)} alt="" />
            <span>{dataSource}</span>
          </div>
          {/* HTML内容渲染 */}
          <div
            className="summary-source-html-content"
            dangerouslySetInnerHTML={{ __html: getSanitizeHTMLString(htmlContentMap[dataSource]) || '' }}
          />
        </div>
      ))}
    </>
  );
};

const DataSourceExplanation: React.FC<IDataSourceExplanationHtmlProps> = (props) => {
  const { domain, triggerBtnText, dataSourceList, htmlContentMap, trigger, body } = props;

  const { formatMessage } = useIntl();

  const [open, setOpen] = useState<boolean>(() => getDataSourceExplanationInitialState(domain));

  const handleToggleOpen = () => {
    const openNew = !open;
    setOpen(openNew);
    setDataSourceExplanationInitialState(domain, openNew);
  };

  return (
    <>
      {/* 触发数据源解释内容展示、隐藏的按钮 */}
      {trigger?.current &&
        createPortal(
          <SourceExplanation
            textValue={triggerBtnText}
            openSource={open}
            handleClickSourceState={handleToggleOpen}
          />,
          trigger?.current,
        )}
      {/* 数据源解释内容展示 */}
      {body?.current &&
        open &&
        createPortal(
          <CardSlotV2
            iconList={[]}
            title={formatMessage({ id: 'dbi_pcconsole_source_differences_explanation' })}
            className={classnames('dbi-benchmark-summary dbi-sroucetips-summary', {
              'is-expand': open,
            })}
          >
            {/* 关闭按钮 */}
            <SvgIcon
              className="dbi-benchmark-summary-close-icon"
              type="clonse"
              onClick={(e) => {
                e?.stopPropagation();
                setOpen(false);
                setDataSourceExplanationInitialState(domain, false);
              }}
            />
            <DataSourceHtmlContent dataSourceList={dataSourceList} htmlContentMap={htmlContentMap} />
          </CardSlotV2>,
          body?.current,
        )}
    </>
  );
};

export default DataSourceExplanation;
